/**
 * Client-side trip stats from GPS track rows (mirrors backend tripActivity + computeTrackStats).
 */

import { normalizeMapTrack, computeWaypointUnderwayMinutes } from './tripGpsResolve';

const MIN_MOVING_SPEED_KNOTS = 1.0;
const MIN_ENGINE_RPM = 300;
const RUNNING_ENGINE_STATES = new Set(['running', 'on', 'active', 'run']);
const ACTIVE_GAP_CAP_MS = 6000;
const ACTIVE_SAMPLE_MS = 5000;

function collectRpms(point) {
  if (!point) return [];
  const rpms = [];
  const add = (v) => {
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) rpms.push(n);
  };
  add(point.engine_rpm);
  add(point.rpm_port);
  add(point.rpm_starboard);
  const engines = point.telemetry?.engines || point.engines || [];
  for (const e of engines) add(e.rpm);
  return rpms;
}

export function isEngineRunning(point) {
  if (!point) return false;
  const engines = point.telemetry?.engines || point.engines || [];
  for (const e of engines) {
    const st = e.state != null ? String(e.state).toLowerCase() : '';
    if (RUNNING_ENGINE_STATES.has(st)) return true;
  }
  return collectRpms(point).some((r) => r >= MIN_ENGINE_RPM);
}

export function isUnderway(point) {
  if (!point) return false;
  const speed = parseFloat(point.speed_knots);
  return Number.isFinite(speed) && speed >= MIN_MOVING_SPEED_KNOTS;
}

export function isTripActivityActive(point) {
  return isUnderway(point) || isEngineRunning(point);
}

function pointTimestampMs(point) {
  const ts = point?.recorded_at || point?.timestamp;
  if (!ts) return NaN;
  return new Date(ts).getTime();
}

export function computeActiveDurationMinutes(rows, opts = {}) {
  const capMs = opts.gapCapMs ?? ACTIVE_GAP_CAP_MS;
  const sampleMs = opts.sampleMs ?? ACTIVE_SAMPLE_MS;
  if (!rows?.length) return 0;
  if (rows.length === 1) {
    return isTripActivityActive(rows[0]) ? Math.max(1, Math.round(sampleMs / 60000)) : 0;
  }

  let totalMs = 0;
  for (let i = 0; i < rows.length - 1; i += 1) {
    const a = rows[i];
    const b = rows[i + 1];
    if (!isTripActivityActive(a) && !isTripActivityActive(b)) continue;
    const t0 = pointTimestampMs(a);
    const t1 = pointTimestampMs(b);
    if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) continue;
    totalMs += Math.min(t1 - t0, capMs);
  }

  const last = rows[rows.length - 1];
  if (isTripActivityActive(last)) totalMs += sampleMs;

  return Math.max(0, Math.round(totalMs / 60000));
}

export function computeTripStatsFromTracks(rows) {
  if (!rows?.length) {
    return {
      pointCount: 0,
      distanceKm: 0,
      distanceNm: 0,
      durationMinutes: 0,
      avgSpeedKnots: 0,
      maxSpeedKnots: 0,
      engineHoursDelta: 0,
      fuelUsed: 0,
    };
  }

  const toRad = (deg) => (deg * Math.PI) / 180;
  const haversineKm = (lat1, lon1, lat2, lon2) => {
    if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return 0;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const pointFuelLph = (row) => {
    const port = row.fuel_rate_port != null ? Number(row.fuel_rate_port) : NaN;
    const stbd = row.fuel_rate_starboard != null ? Number(row.fuel_rate_starboard) : NaN;
    if (Number.isFinite(port) || Number.isFinite(stbd)) {
      return (Number.isFinite(port) ? port : 0) + (Number.isFinite(stbd) ? stbd : 0);
    }
    const engines = row.telemetry?.engines || row.engines || [];
    let sum = 0;
    let any = false;
    for (const e of engines) {
      const v = e?.fuelLph != null ? Number(e.fuelLph) : NaN;
      if (Number.isFinite(v)) {
        sum += v;
        any = true;
      }
    }
    if (any) return sum;
    const flow = row.fuel_flow_lph != null ? Number(row.fuel_flow_lph) : NaN;
    return Number.isFinite(flow) ? flow : null;
  };

  const normalizeHours = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n > 100000 ? n / 3600 : n;
  };

  let distanceKm = 0;
  let speedSum = 0;
  let speedCount = 0;
  let maxSpeedKnots = 0;
  const engineHours = [];
  let fuelUsed = 0;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const speed = Number(row.speed_knots) || 0;
    if (speed > 0) {
      speedSum += speed;
      speedCount += 1;
      if (speed > maxSpeedKnots) maxSpeedKnots = speed;
    }
    [row.hours_port, row.hours_starboard, row.engine_hours].forEach((h) => {
      const v = normalizeHours(h);
      if (v != null) engineHours.push(v);
    });
    (row.telemetry?.engines || []).forEach((e) => {
      const v = normalizeHours(e?.hours);
      if (v != null) engineHours.push(v);
    });

    if (i === 0) continue;
    const prev = rows[i - 1];
    distanceKm += haversineKm(
      parseFloat(prev.latitude),
      parseFloat(prev.longitude),
      parseFloat(row.latitude),
      parseFloat(row.longitude)
    );

    const a = pointFuelLph(prev);
    const b = pointFuelLph(row);
    if (a == null && b == null) continue;
    const fa = a != null ? a : b;
    const fb = b != null ? b : a;
    const t0 = pointTimestampMs(prev);
    const t1 = pointTimestampMs(row);
    if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) continue;
    const hours = (t1 - t0) / 3600000;
    if (hours > 0 && hours <= 2) fuelUsed += ((fa + fb) / 2) * hours;
  }

  let durationMinutes = computeActiveDurationMinutes(rows);

  let engineHoursDelta = 0;
  if (engineHours.length >= 2) {
    engineHoursDelta = Math.max(0, Math.max(...engineHours) - Math.min(...engineHours));
    if (engineHoursDelta > 168) engineHoursDelta = 0;
  }

  let avgSpeedKnots = speedCount > 0 ? speedSum / speedCount : 0;
  const distanceNm = distanceKm * 0.539957;
  if (avgSpeedKnots <= 0 && durationMinutes > 0 && distanceNm > 0) {
    avgSpeedKnots = distanceNm / (durationMinutes / 60);
  }

  return {
    pointCount: rows.length,
    distanceKm,
    distanceNm,
    durationMinutes,
    avgSpeedKnots,
    maxSpeedKnots,
    engineHoursDelta,
    fuelUsed,
  };
}

/** Wall-clock span from first/last GPS timestamps. */
export function computeTrackSpanDurationMinutes(rows) {
  if (!rows?.length) return 0;
  const times = rows
    .map((row) => pointTimestampMs(row))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (times.length < 2) return 0;
  return Math.max(1, Math.round((times[times.length - 1] - times[0]) / 60000));
}

/** Stats from map polyline [[lat, lon], ...] when telemetry rows are missing. */
export function computeTripStatsFromPolyline(track) {
  const normalized = normalizeMapTrack(track);
  if (normalized.length < 2) return null;
  const rows = normalized.map(([lat, lon]) => ({
    latitude: lat,
    longitude: lon,
    recorded_at: null,
    speed_knots: 0,
  }));
  const stats = computeTripStatsFromTracks(rows);
  if (stats.avgSpeedKnots <= 0 && stats.durationMinutes > 0 && stats.distanceNm > 0) {
    stats.avgSpeedKnots = stats.distanceNm / (stats.durationMinutes / 60);
  }
  return stats;
}

/** Trip wall clock (slip away → return) — not run time. */
export function tripWallClockMinutes(trip) {
  if (!trip?.start_time || !trip?.end_time) return 0;
  const t0 = new Date(trip.start_time).getTime();
  const t1 = new Date(trip.end_time).getTime();
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) return 0;
  return Math.max(1, Math.round((t1 - t0) / 60000));
}

export function isTripWallClockMinutes(minutes, trip) {
  const wall = tripWallClockMinutes(trip);
  const m = parseInt(minutes, 10);
  if (!wall || !Number.isFinite(m) || m <= 0) return false;
  return Math.abs(m - wall) <= 2;
}

/** Prefer active underway/engine time; never slip-to-slip wall clock. */
export function pickRunTimeMinutes({
  activeMinutes = 0,
  waypointMinutes = 0,
  storedMinutes = 0,
  spanMinutes = 0,
  trip = null,
} = {}) {
  const active = parseInt(activeMinutes, 10);
  if (active > 0) return active;

  const waypoint = parseInt(waypointMinutes, 10);
  if (waypoint > 0) return waypoint;

  const stored = parseInt(storedMinutes, 10);
  if (stored > 0 && !isTripWallClockMinutes(stored, trip)) return stored;

  // GPS first→last span when speed/RPM samples were missing (lite route load).
  // Cap at 24h so multi-day dock recordings are not shown as "run time".
  const span = parseInt(spanMinutes, 10);
  if (span > 0 && span <= 24 * 60 && !isTripWallClockMinutes(span, trip)) return span;
  if (span > 0 && span <= 24 * 60) return span;

  return 0;
}

/**
 * Merge API stats, telemetry rows, and map polyline into display-ready trip stats.
 * API often returns duration without distance — never skip polyline distance when track exists.
 */
export function resolveTripDisplayStats({
  trip = null,
  routeStats = null,
  track = [],
  points = [],
  waypoints = [],
  routeStartTime = null,
  routeEndTime = null,
} = {}) {
  const polyline = normalizeMapTrack(track);
  let stats = routeStats && typeof routeStats === 'object' ? { ...routeStats } : null;

  if (points.length > 0) {
    const fromPoints = computeTripStatsFromTracks(points);
    stats = {
      ...(stats || {}),
      ...fromPoints,
      distanceKm: fromPoints.distanceKm > 0 ? fromPoints.distanceKm : (stats?.distanceKm || 0),
      distanceNm: fromPoints.distanceNm > 0 ? fromPoints.distanceNm : (stats?.distanceNm || 0),
      pointCount: Math.max(stats?.pointCount || 0, fromPoints.pointCount || 0),
      durationMinutes: fromPoints.durationMinutes > 0
        ? fromPoints.durationMinutes
        : (stats?.durationMinutes || 0),
      avgSpeedKnots: fromPoints.avgSpeedKnots > 0
        ? fromPoints.avgSpeedKnots
        : (stats?.avgSpeedKnots || 0),
      fuelUsed: fromPoints.fuelUsed > 0 ? fromPoints.fuelUsed : (stats?.fuelUsed || 0),
      engineHoursDelta: fromPoints.engineHoursDelta > 0
        ? fromPoints.engineHoursDelta
        : (stats?.engineHoursDelta || 0),
    };
  }

  if (polyline.length > 1) {
    const fromPolyline = computeTripStatsFromPolyline(polyline);
    if (fromPolyline) {
      stats = {
        ...(stats || {}),
        distanceKm: fromPolyline.distanceKm > 0 ? fromPolyline.distanceKm : (stats?.distanceKm || 0),
        distanceNm: fromPolyline.distanceNm > 0 ? fromPolyline.distanceNm : (stats?.distanceNm || 0),
        pointCount: Math.max(stats?.pointCount || 0, fromPolyline.pointCount || 0, polyline.length),
        // Polyline has no timestamps/speed — never wipe a real duration with 0
        durationMinutes: (stats?.durationMinutes > 0)
          ? stats.durationMinutes
          : (fromPolyline.durationMinutes || 0),
        avgSpeedKnots: stats?.avgSpeedKnots > 0 ? stats.avgSpeedKnots : fromPolyline.avgSpeedKnots,
        maxSpeedKnots: Math.max(stats?.maxSpeedKnots || 0, fromPolyline.maxSpeedKnots || 0),
      };
    }
  }

  if (!stats && polyline.length > 0) {
    stats = computeTripStatsFromTracks(polyline.map(([lat, lon]) => ({
      latitude: lat,
      longitude: lon,
      recorded_at: routeStartTime || null,
      speed_knots: 0,
    })));
  }

  const activeFromPoints = points.length > 0 ? computeActiveDurationMinutes(points) : 0;
  const activeFromStats = stats?.durationMinutes && !isTripWallClockMinutes(stats.durationMinutes, trip)
    ? stats.durationMinutes
    : 0;

  let spanMinutes = points.length > 1 ? computeTrackSpanDurationMinutes(points) : 0;
  if (spanMinutes <= 0 && routeStartTime && routeEndTime) {
    const t0 = new Date(routeStartTime).getTime();
    const t1 = new Date(routeEndTime).getTime();
    if (Number.isFinite(t0) && Number.isFinite(t1) && t1 > t0) {
      spanMinutes = Math.max(1, Math.round((t1 - t0) / 60000));
    }
  }

  const runMinutes = pickRunTimeMinutes({
    activeMinutes: Math.max(activeFromPoints, activeFromStats),
    waypointMinutes: computeWaypointUnderwayMinutes(waypoints),
    storedMinutes: trip?.run_time_minutes,
    spanMinutes: (stats?.distanceNm || 0) > 0.2 ? spanMinutes : 0,
    trip,
  });

  if (stats) {
    stats.durationMinutes = runMinutes;
    if (stats.avgSpeedKnots <= 0 && stats.durationMinutes > 0 && stats.distanceNm > 0) {
      stats.avgSpeedKnots = stats.distanceNm / (stats.durationMinutes / 60);
    }
  }

  return stats || {
    pointCount: 0,
    distanceKm: 0,
    distanceNm: 0,
    durationMinutes: 0,
    avgSpeedKnots: 0,
    maxSpeedKnots: 0,
    engineHoursDelta: 0,
    fuelUsed: 0,
  };
}

/** Prefer active run time from track/waypoints — never slip-to-slip wall clock. */
export function getTripRunTimeMinutes(trip, derivedStats) {
  const fromDerived = derivedStats?.durationMinutes;
  if (fromDerived > 0 && !isTripWallClockMinutes(fromDerived, trip)) {
    return fromDerived;
  }
  const run = parseInt(trip?.run_time_minutes, 10);
  if (run > 0 && !isTripWallClockMinutes(run, trip)) return run;
  if (fromDerived > 0) return fromDerived;
  return 0;
}

/** Hide lifetime engine-hour counters that were stored by mistake. */
export function formatTripEngineHours(storedHours, computedDelta) {
  const stored = parseFloat(storedHours);
  if (Number.isFinite(computedDelta) && computedDelta > 0 && computedDelta <= 168) {
    return computedDelta.toFixed(1);
  }
  if (Number.isFinite(stored) && stored > 0 && stored <= 168) {
    return stored.toFixed(1);
  }
  return '0.0';
}
